// ─── Domain types ────────────────────────────────────────────────────────────

export type Plan = 'free' | 'premium' | 'premium_monthly' | 'premium_annual';

export type RecurringType = 'none' | 'weekly' | 'monthly' | 'yearly';

export const RECURRING_TYPE_META: Record<
  RecurringType,
  { label: string; labelShort: string; icon: string }
> = {
  none:    { label: 'Non ricorrente', labelShort: '',           icon: '' },
  weekly:  { label: 'Settimanale',    labelShort: '/settimana', icon: '📅' },
  monthly: { label: 'Mensile',        labelShort: '/mese',      icon: '🗓️' },
  yearly:  { label: 'Annuale',        labelShort: '/anno',      icon: '📆' },
};

export const RECURRING_TYPES = Object.keys(RECURRING_TYPE_META) as RecurringType[];

export type Category = string;

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  plan: Plan;
  onboarding_completed?: boolean;
  onboarding_use_case?: 'couples' | 'freelance' | 'personal' | null;
  trial_end?: string | null;
  next_billing_at?: string | null;
  invite_code?: string | null;
  referrals_count?: number;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface GemmaAdvice {
  tips: string[];
  wastePotentialYearly: number;
}

export interface ParsedPayslip {
  period: string | null;
  grossIncome: number;
  netIncome: number;
  taxes: {
    irpef: number;
    inps: number;
    addizionali: number;
    other: number;
  };
  entries: Array<{
    kind: 'income' | 'expense';
    description: string;
    amount: number;
    category?: string;
  }>;
  confidence: number;
}

export interface Expense {
  id: string;
  user_id: string;
  description: string;
  amount: number;
  category: Category;
  recurring: boolean;
  recurring_type: RecurringType;
  next_due: string | null;
  frequency_days: number | null;
  is_template: boolean;
  template_id: string | null;
  date: string;
  created_at: string;
  updated_at: string;
}

export interface Income {
  id: string;
  user_id: string;
  source: string;
  amount: number;
  recurring: boolean;
  recurring_type: RecurringType;
  date: string;
  created_at: string;
  updated_at: string;
}

export interface Goal {
  id: string;
  user_id: string;
  name: string;
  emoji: string;
  target_amount: number;
  current_amount: number;
  deadline: string | null;
  created_at: string;
  updated_at: string;
}

export interface CategoryBudget {
  id: string;
  user_id: string;
  category: string;
  monthly_limit: number;
  created_at: string;
  updated_at: string;
}

export interface BudgetAlert {
  id: string;
  user_id: string;
  category: string;
  month_start: string;
  budget_limit: number;
  spent_amount: number;
  exceeded_amount: number;
  message: string;
  notified_email_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExpenseFormData {
  description: string;
  amount: number;
  category: Category;
  recurring: boolean;
  recurring_type: RecurringType;
  next_due: string | null;
  frequency_days: number | null;
  date: string;
}

export interface ExpenseFilters {
  search: string;
  category: Category | 'all';
  dateFrom: string;
  dateTo: string;
  recurring: 'all' | 'true' | 'false';
}

// ─── Analytics types ─────────────────────────────────────────────────────────

export interface CategoryStat {
  category: Category;
  total: number;
  count: number;
  percentage: number;
}

export interface MonthStat {
  month: string;       // "YYYY-MM"
  label: string;       // "Jan 2025"
  total: number;
  count: number;
}

export interface Analytics {
  totalSpent: number;
  currentMonthSpent: number;
  previousMonthSpent: number;
  monthlyChange: number;       // percentage
  recurringTotal: number;
  topCategory: Category | null;
  byCategory: CategoryStat[];
  byMonth: MonthStat[];
  expenseCount: number;
  premiumVisuals: PremiumVisuals | null;
}

export interface SankeyNode {
  name: string;
}

export interface SankeyLink {
  source: number;
  target: number;
  value: number;
  category?: string;
}

export interface SavingsLinePoint {
  month: string;
  label: string;
  income: number;
  spent: number;
  saving: number;
}

export interface PremiumVisuals {
  sankey: {
    nodes: SankeyNode[];
    links: SankeyLink[];
  };
  savingsLine: SavingsLinePoint[];
}

// ─── Category metadata ───────────────────────────────────────────────────────

export const CATEGORY_META: Record<
  Category,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  food:          { label: 'Food & Dining',  color: '#f97316', bgColor: 'rgba(249,115,22,0.15)',  icon: '🍔' },
  transport:     { label: 'Transport',      color: '#3b82f6', bgColor: 'rgba(59,130,246,0.15)',  icon: '🚗' },
  housing:       { label: 'Housing',        color: '#8b5cf6', bgColor: 'rgba(139,92,246,0.15)',  icon: '🏠' },
  entertainment: { label: 'Entertainment',  color: '#ec4899', bgColor: 'rgba(236,72,153,0.15)',  icon: '🎬' },
  health:        { label: 'Health',         color: '#10b981', bgColor: 'rgba(16,185,129,0.15)',  icon: '❤️' },
  education:     { label: 'Education',      color: '#f59e0b', bgColor: 'rgba(245,158,11,0.15)',  icon: '📚' },
  shopping:      { label: 'Shopping',       color: '#6366f1', bgColor: 'rgba(99,102,241,0.15)',  icon: '🛍️' },
  utilities:     { label: 'Utilities',      color: '#14b8a6', bgColor: 'rgba(20,184,166,0.15)',  icon: '⚡' },
  other:         { label: 'Other',          color: '#6b7280', bgColor: 'rgba(107,114,128,0.15)', icon: '📦' },
};

export const CATEGORIES = Object.keys(CATEGORY_META) as Category[];


